/* Schedule, venues, credits and flags are static JSON files, not services (D12).
   They are fetched, cached in localStorage, and served stale-then-fresh: if we
   have a copy, it goes on screen immediately and the network updates it
   underneath. Principle 3 — never show a spinner where cached content exists. */

import { useEffect, useState } from 'react';

/**
 * `men`/`women` are how the couple actually wrote the dress code, and folding
 * them into one `full` paragraph made it unscannable at exactly the moment it
 * gets read — standing in front of a suitcase. Both stay optional: an event
 * whose guidance is the same for everyone (the holi hour: "clothes you don't
 * mind ruining") uses `full` alone and renders as one line.
 */
export interface DressCode {
  short: string;
  full?: string;
  men?: string;
  women?: string;
  tags?: string[];
}

/**
 * A collaborative playlist attached to an event (D57).
 *
 * Content rather than code, like everything else in this file (D12), so the
 * couple can paste the link — or pull it — from a phone without a deploy. It
 * ships in `schedule.json` with an **empty `url`** so the shape is visible to
 * whoever fills it in, which is why `playlistOf` below treats blank as absent:
 * a half-filled key must render nothing, never a dead link.
 *
 * A link and not an embed, deliberately. Spotify's iframe is playback-only —
 * there is no add-to-playlist control inside it — so an embed would show guests
 * the playlist and give them no way to contribute, which is the entire point.
 * The link opens the app, where adding works.
 */
export interface EventPlaylist {
  url: string;
  /** The playlist's real name. Defaults to "After party playlist". */
  title?: string;
  /** The playlist's own description, which doubles as the call to action. */
  description?: string;
  /**
   * The playlist's cover art, served from `public/content/` like the wardrobe
   * PDF so it can be swapped without a deploy (D13). Square — every playlist
   * cover is — and small: it renders at 40px on Now and 64px on the event page.
   */
  coverUrl?: string;
  /** The fine print, on the event page only. Where to say it is collaborative,
   *  and what to do without Spotify. */
  note?: string;
}

export interface WeddingEvent {
  id: string;
  name: string;
  start: string;
  end?: string;
  venueId: string;
  dressCode?: DressCode;
  notes?: string;
  playlist?: EventPlaylist;
}

/**
 * The name to show when the content has not supplied one.
 *
 * Here rather than in either component, because both render it and neither is
 * the other's owner — it lived in `PlaylistCard` for one commit and
 * `EventDetail` immediately re-typed it as a literal, which is the whole of the
 * argument.
 */
export const PLAYLIST_FALLBACK_TITLE = 'After party playlist';

/** A playlist counts only once it has a URL — see the note above on why the
 *  key exists before the link does. */
export const playlistOf = (event: WeddingEvent): EventPlaylist | null =>
  (event.playlist?.url?.trim() ? event.playlist : null);

export interface Wardrobe { title: string; intro: string; weather?: string; pdfUrl?: string }

/**
 * Something on offer during the week that is not part of the programme — the
 * daily massages, and whatever gets added beside them.
 *
 * **Deliberately not a `WeddingEvent`.** It has a time and a venue and would
 * model perfectly well as one, and that is the trap: every screen that reads
 * `events` would pick it up, and the Schedule would list a massage between the
 * haldi and the sangeet as though a guest had to choose. These are the opposite
 * of the programme — optional, repeating, and nobody's day is built around
 * them — so they are their own list, read by `ExtraNote` and nothing else.
 *
 * `venueId` rather than a name so one spelling wins and the row can link to the
 * map, the same as an event does.
 */
export interface Extra {
  id: string;
  name: string;
  venueId: string;
  start: string;
  end?: string;
  /** Anything a guest has to do first. Printed after the venue, if present. */
  note?: string;
}

export interface Schedule {
  version: string;
  timezone: string;
  wardrobe: Wardrobe;
  events: WeddingEvent[];
  /** Absent until somebody adds one — see `Extra`. */
  extras?: Extra[];
}

export interface Venue {
  id: string; name: string; x: number; y: number; where: string;
  indoor: boolean; floor?: string; navUrl: string;
}
export interface VenueDoc {
  version: string;
  resort: {
    name: string;
    planLabel: string;
    /** Which wing labels to pick out on the plan. */
    stayWings?: string[];
    /** Wing label → the OSM building ids it is made of. Content rather than
     *  code so it can be fixed without a deploy; see the note in venue.json. */
    wings?: Record<string, number[]>;
  };
  venues: Venue[];
}

/**
 * `line` is the couple's own sentence about that vendor, and it is optional
 * because a blank one is honest and an invented one is not. The card reads
 * perfectly well as name + role until they write it.
 */
export interface CreditPerson { name: string; title: string; line?: string; handle?: string; photoUrl?: string }
export interface Credits { version: string; thanks: string; groups: { role: string; people: CreditPerson[] }[] }

export interface Flags {
  READ_ONLY: boolean; readOnlyMessage: string;
  galleryEnabled: boolean; displayEnabled: boolean;
  /** Show a real GPS dot on the resort plan. Off until the venue and wing
   *  identifications in venue.json's `$tbc` are confirmed: the *position* is
   *  exact by construction, but a dot is only useful beside a building someone
   *  can name, and a precise dot next to a mislabelled building is worse than
   *  no dot at all. Optional so an older cached flags.json stays valid. */
  liveLocation?: boolean;
  /** Gates `/map?pick`, the coordinate tool. A build tool, so off — but gated
   *  by content rather than by a build flag, because it is most needed by
   *  someone standing in the resort with a phone. Optional so an older cached
   *  flags.json stays valid. */
  mapPicker?: boolean;
}

export interface Cached<T> {
  data: T | null;
  fetchedAt: number | null;
  /** True when what you are looking at came from cache and the network has not confirmed it. */
  stale: boolean;
  error: Error | null;
}

const CACHE_PREFIX = 'mv.content.';

function readCache<T>(name: string): { data: T; fetchedAt: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + name);
    if (!raw) return null;
    return JSON.parse(raw) as { data: T; fetchedAt: number };
  } catch {
    return null;
  }
}

function writeCache<T>(name: string, data: T): number {
  const fetchedAt = Date.now();
  try {
    localStorage.setItem(CACHE_PREFIX + name, JSON.stringify({ data, fetchedAt }));
  } catch {
    /* A full quota is not worth an error to a guest — the fetch already worked. */
  }
  return fetchedAt;
}

export function useContent<T>(name: string): Cached<T> {
  const [state, setState] = useState<Cached<T>>(() => {
    const cached = readCache<T>(name);
    return cached
      ? { data: cached.data, fetchedAt: cached.fetchedAt, stale: true, error: null }
      : { data: null, fetchedAt: null, stale: false, error: null };
  });

  useEffect(() => {
    let live = true;
    fetch(`/content/${name}.json`, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`${name}: ${r.status}`);
        return r.json() as Promise<T>;
      })
      .then((data) => {
        if (!live) return;
        const fetchedAt = writeCache(name, data);
        setState({ data, fetchedAt, stale: false, error: null });
      })
      .catch((error: Error) => {
        if (!live) return;
        // Offline with a cache is a state, not an error (principle 4). Keep what
        // is on screen and let the offline pill explain the timestamp.
        setState((prev) => (prev.data ? { ...prev, stale: true } : { ...prev, error }));
      });
    return () => { live = false; };
  }, [name]);

  return state;
}

export const useSchedule = () => useContent<Schedule>('schedule');
export const useVenues = () => useContent<VenueDoc>('venue');
export const useCredits = () => useContent<Credits>('credits');
export const useFlags = () => useContent<Flags>('flags');
